import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { StatCard } from '../../../components/ui';
import { getSummary, inr } from '../data';
import type { ReconSummary } from '../types';

const BUCKET_COLOR: Record<string, 'red' | 'orange' | 'purple' | 'green'> = {
	overdue_with_trail: 'orange',
	overdue_no_trail: 'red',
	paid_no_bill: 'purple',
	ok: 'green',
};

/** Reconciliation overview — every headline number is a COUNT/SUM from a source table. */
export const Overview: React.FC = () => {
	const [s, setS] = useState<ReconSummary | null>(null);
	const [err, setErr] = useState<string | null>(null);

	useEffect(() => {
		let on = true;
		getSummary()
			.then(x => on && setS(x))
			.catch(e => on && setErr(e?.message || 'Failed to load'));
		return () => {
			on = false;
		};
	}, []);

	if (err) return <div className='p-6 text-red-600'>Error: {err}</div>;
	if (!s)
		return (
			<div className='flex items-center gap-2 p-6 text-gray-500'>
				<Loader2 className='animate-spin' size={18} /> Loading reconciliation summary…
			</div>
		);

	return (
		<div className='space-y-6'>
			<section>
				<h3 className='text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3'>
					Accounts Payable — vendors
				</h3>
				<div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
					<StatCard icon='📄' label='AP bills' value={s.ap.bills} color='blue' />
					<StatCard icon='⏳' label='Overdue bills' value={s.ap.overdue_bills} color='orange' />
					<StatCard icon='💰' label='Overdue value' value={inr(s.ap.overdue_value)} color='red' />
					<StatCard icon='🏢' label='Vendors' value={s.vendors.total} color='purple' />
				</div>
			</section>

			<section>
				<h3 className='text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3'>
					Accounts Receivable — clients
				</h3>
				<div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
					<StatCard icon='🧾' label='Open invoices' value={s.ar.open_invoices} color='blue' />
					<StatCard icon='💵' label='Outstanding' value={inr(s.ar.outstanding)} color='orange' />
					<StatCard
						icon='⚠️'
						label='Void w/ balance'
						value={s.ar.void_with_balance}
						color='red'
					/>
					<StatCard
						icon='🔴'
						label='Void balance value'
						value={inr(s.ar.void_balance_value)}
						color='red'
					/>
				</div>
			</section>

			<section>
				<h3 className='text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3'>
					Vendor reconciliation buckets
				</h3>
				<div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
					{s.vendors.buckets.map(b => (
						<div
							key={b.bucket}
							className='p-4 rounded-lg border border-gray-200 bg-white shadow-sm'>
							<div
								className={`text-xs font-semibold uppercase tracking-wide mb-1 ${
									BUCKET_COLOR[b.bucket] === 'red'
										? 'text-red-600'
										: BUCKET_COLOR[b.bucket] === 'orange'
										? 'text-orange-600'
										: BUCKET_COLOR[b.bucket] === 'purple'
										? 'text-purple-600'
										: 'text-green-600'
								}`}>
								{b.label}
							</div>
							<div className='text-2xl font-bold text-gray-900'>{b.vendors}</div>
							<div className='text-sm text-gray-500'>
								{b.bucket === 'paid_no_bill'
									? `${inr(b.paid)} paid`
									: `${inr(b.overdue_value || b.outstanding)} exposed`}
							</div>
						</div>
					))}
				</div>
			</section>

			<section>
				<h3 className='text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3'>
					Evidence base
				</h3>
				<div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
					<StatCard
						icon='📧'
						label='Classified threads'
						value={s.email.classified_threads}
						color='blue'
					/>
					<StatCard icon='🔗' label='Email↔Zoho links' value={s.email.zoho_links} color='purple' />
					<StatCard icon='🏦' label='Bank rows parsed' value={s.bank.rows} color='green' />
					<StatCard icon='🧮' label='Reimbursement claims' value={s.reimbursements.claims} color='orange' />
				</div>
				<p className='text-xs text-gray-400 mt-3'>
					Window {s.window.from} → {s.window.to}. LLM audit: {s.llm_audit.calls.toLocaleString()}{' '}
					calls, {inr(s.llm_audit.cost_inr)} total spend. Read-only lens over Zoho (system of
					record), email, bank and the reimbursement sheet.
				</p>
			</section>
		</div>
	);
};
